/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React from "react";
import moment from "moment";
import Button from "../../../components/Button";
import { strings } from "../../../common/Strings/Strings";
import { ChevronLeft } from "../../../common/Icons/ChevronLeft";
import { ChevronRight } from "../../../common/Icons/ChevronRight";
import Tag, { TagType } from "../../../components/Tag";
import { ViewTypes } from "../MainCalendar";
import AddDropdown from "./AddDropdown";
import { momentDateFormat } from "../calendarConfig";

interface Props {
  selectedDay: string;
  setSelectedDay(newDay: string): void;
  setViewType(value: ViewTypes): void;

  openNewReservationModal(): void;
  openNewShiftModal(): void;
  setIsEmergency(value: boolean): void;
  viewType?: ViewTypes;
}

const NavigationMobile: React.FC<Props> = ({
  selectedDay,
  setSelectedDay,
  setViewType,
  openNewReservationModal,
  openNewShiftModal,
  setIsEmergency,
  viewType,
}: Props) => {
  const next = (where: string): void => {
    let nextDate = moment(new Date()).format(momentDateFormat);
    let days = 1;

    if (viewType === ViewTypes.SHIFTLISTVIEW) {
      days = 7;
    } else if (viewType === ViewTypes.YEAR) {
      days = 365;
    }

    if (where === "left") {
      nextDate = moment(selectedDay).subtract(days, "d").format(momentDateFormat);
    }

    if (where === "right") {
      nextDate = moment(selectedDay).add(days, "d").format(momentDateFormat);
    }
    setSelectedDay(nextDate);
  };

  const getTitle = (): string => {
    switch (viewType) {
      case ViewTypes.LISTVIEW:
        return `${moment(selectedDay).format("ll")} (${moment(selectedDay).format("dd")})`;
      case ViewTypes.SHIFTLISTVIEW: {
        let weekTitle = moment(selectedDay).weekday(0).format("YYYY. MMM");
        if (!moment(selectedDay).weekday(0).isSame(moment(selectedDay).weekday(6), "month")) {
          weekTitle += ` - ${moment(selectedDay).weekday(6).format("MMM")}`;
        }
        return `${weekTitle} (${moment(selectedDay).format("wo")} ${strings.week})`;
      }
      case ViewTypes.YEAR: {
        return moment(selectedDay).format("YYYY");
      }
      default:
        return `${moment(selectedDay).format("ll")} (${moment(selectedDay).format("dd")})`;
    }
  };

  return (
    <>
      <div className="flex flex-nowrap lg:flex-nowrap lg:grid lg:grid-cols-3 items-center">
        <div className="flex items-center space-x-4 justify-start">
          <div className="flex">
            <button
              type="button"
              onClick={() => {
                setSelectedDay(moment(new Date()).format(momentDateFormat));
              }}
            >
              <span className="tw-link-tercier">{strings.today}</span>
            </button>
          </div>
          <div className="flex space-x-4">
            <button
              type="button"
              className="tw-icon tw-link"
              onClick={() => {
                next("left");
              }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="tw-icon tw-link"
              onClick={() => {
                next("right");
              }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <h1 className="text-lg font-medium leading-tight text-zinc-800 dark:text-white pr-3">{getTitle()}</h1>
        </div>
        <div className="flex items-center space-x-4 justify-end ml-auto">
          <AddDropdown
            openNewReservationModal={openNewReservationModal}
            openNewShiftModal={openNewShiftModal}
            setIsEmergency={setIsEmergency}
          />
        </div>
      </div>
      <div className="flex space-x-3">
        <Button
          onClick={() => {
            setViewType(ViewTypes.LISTVIEW);
          }}
          variant="basic"
        >
          <Tag
            text={strings.list}
            type={viewType === ViewTypes.LISTVIEW ? TagType.secondary : TagType.secondaryOutlined}
          />
        </Button>
        <Button
          onClick={() => {
            setViewType(ViewTypes.DAY);
          }}
          variant="basic"
        >
          <Tag text={strings.day} type={viewType === ViewTypes.DAY ? TagType.secondary : TagType.secondaryOutlined} />
        </Button>
        <Button
          onClick={() => {
            setViewType(ViewTypes.SHIFTLISTVIEW);
          }}
          variant="basic"
        >
          <Tag
            text={strings.week}
            type={viewType === ViewTypes.SHIFTLISTVIEW ? TagType.secondary : TagType.secondaryOutlined}
          />
        </Button>
        <Button
          onClick={() => {
            setViewType(ViewTypes.YEAR);
          }}
          variant="basic"
        >
          <Tag text={strings.year} type={viewType === ViewTypes.YEAR ? TagType.secondary : TagType.secondaryOutlined} />
        </Button>
      </div>
    </>
  );
};

export default NavigationMobile;
